/**
 * Dashboard Data Hook
 * 
 * Manages the single dashboard API call and distributes data to components
 */

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { getDashboardData } from '../api/api';
import type { DashboardDataResponse } from '../types/api-models';


export const useDashboardData = () => {
  const [data, setData] = useState<DashboardDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(0);

  const fetchDashboardData = useCallback(async (forceRefresh: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getDashboardData();
      setData(response);
      setLastFetch(Date.now());
      return response;
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(errorMsg);
      message.error(errorMsg);
      console.error('[Dashboard] Error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch on mount only
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Refresh function for manual refresh
  const refresh = useCallback(() => {
    return fetchDashboardData(true);
  }, [fetchDashboardData]);

  // Helper functions to get specific data sections
  const getActivityTrendData = useCallback(() => {
    if (!data) return null;
    return data.activity_trend;
  }, [data]);

  const getCostTrendData = useCallback(() => {
    if (!data) return null;
    return data.cost_trend;
  }, [data]);

  const getFeedbackAnalysisData = useCallback(() => {
    if (!data) return null;
    return data.feedback_analysis;
  }, [data]);

  const getEngagementFunnelData = useCallback(() => {
    if (!data) return null;
    return data.engagement_funnel;
  }, [data]);

  const getCategoryDistributionData = useCallback(() => {
    if (!data) return null;
    return data.category_distribution;
  }, [data]);

  const getSemanticKeywordsData = useCallback(() => {
    if (!data) return null;
    return data.semantic_keywords;
  }, [data]);

  const getSemanticTopicsData = useCallback(() => {
    if (!data) return null;
    return data.semantic_topics;
  }, [data]);

  const getAITrendsData = useCallback(() => {
    if (!data) return null;
    return data.ai_trends;
  }, [data]);

  return {
    data,
    loading,
    error,
    refresh,
    getActivityTrendData,
    getCostTrendData,
    getFeedbackAnalysisData,
    getEngagementFunnelData,
    getCategoryDistributionData,
    getSemanticKeywordsData,
    getSemanticTopicsData,
    getAITrendsData
  };
};
